import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CalendarDays, CalendarPlus, MapPin, Pencil, Trash2, Music2, Users, Share2,
  Check, X as XIcon, HelpCircle, Navigation, Shirt, ClipboardList,
  MessageSquare, SendHorizontal, ListMusic, ArrowLeftRight, FolderOpen,
  Sun, CloudSun, Cloudy, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
} from "lucide-react";
import api, { eventCoverUrl } from "@/lib/api";
import { Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ConfirmDialog from "@/components/ConfirmDialog";
import { NavChooser } from "@/components/NavChooser";
import { hasGeo, osmEmbedUrl } from "@/lib/geo";
import { dateLocale } from "@/lib/i18n";
import { toast } from "sonner";

const CAT_CLS = {
  repetition: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  prestation: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  autre: "bg-sky-500/15 text-sky-400 border-sky-500/30",
};
const LEGACY_MAIN = { concert: "prestation", concours: "prestation", evenement: "autre" };
const mainOf = (k) => (CAT_CLS[k] ? k : LEGACY_MAIN[k] || "autre");

const WX_ICON = {
  clear: Sun, partly: CloudSun, cloudy: Cloudy, fog: CloudFog,
  drizzle: CloudDrizzle, rain: CloudRain, showers: CloudRain,
  snow: CloudSnow, storm: CloudLightning,
};

// Phase 1 refonte Agenda : fiche détaillée d'un événement (NIVEAU 2).
// Réutilise uniquement les données existantes (pas de nouvelle infrastructure).
export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, group, t, lang } = useAuth();
  const isAdmin = user?.role === "admin";
  const canProgram = ["admin", "music_leader"].includes(user?.role);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [navGeo, setNavGeo] = useState(null);
  const [invItems, setInvItems] = useState([]);
  const [comments, setComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [delComment, setDelComment] = useState(null);
  const [weather, setWeather] = useState(null);

  const load = useCallback(() => {
    api.get(`/events/${eventId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const st = e?.status || e?.response?.status;
        if (st === 404) setNotFound(true);
        else toast.error(e.friendly || t("common.error"));
      });
    api.get(`/events/${eventId}/comments`).then((r) => setComments(r.data)).catch(() => setComments([]));
    api.get(`/events/${eventId}/weather`).then((r) => setWeather(r.data)).catch(() => setWeather(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/inventory").then((r) => setInvItems(r.data.items || [])).catch(() => {}); }, []);

  const answer = async (status) => {
    const prev = data.my_status;
    if (prev === status) return;
    try {
      await api.post(`/events/${eventId}/availability`, { status });
      setData((d) => {
        const c = { ...d.counts };
        if (prev) c[prev] = Math.max(0, (c[prev] || 0) - 1);
        c[status] = (c[status] || 0) + 1;
        c.unanswered = Math.max(0, c.total - c.yes - c.maybe - c.no);
        return { ...d, my_status: status, counts: c };
      });
    } catch (e) { toast.error(e.friendly); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/events/${eventId}`);
      toast.success(t("agd.deleted"));
      navigate("/agenda");
    } catch (e) { toast.error(e.friendly); }
  };

  const relTime = (iso) => {
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return t("agd.time_now");
    if (m < 60) return t("agd.time_min", { count: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t("agd.time_hours", { count: h });
    return t("agd.time_days", { count: Math.floor(h / 24) });
  };

  const initials = (name) => (name || "?").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  const sendComment = async () => {
    const content = commentText.trim();
    if (!content) return;
    try {
      const r = await api.post(`/events/${eventId}/comments`, { content, parent_comment_id: replyTo?.id || null });
      setComments((cs) => [...(cs || []), r.data]); // ajout immédiat, sans recharger la fiche
      setCommentText("");
      setReplyTo(null);
    } catch (e) { toast.error(e.friendly); }
  };

  const doDeleteComment = async () => {
    try {
      await api.delete(`/comments/${delComment.id}`);
      setComments((cs) => cs.filter((c) => c.id !== delComment.id && c.parent_comment_id !== delComment.id));
      setDelComment(null);
    } catch (e) { toast.error(e.friendly); }
  };

  if (notFound) {
    return (
      <div className="w-full max-w-2xl mx-auto py-16 text-center space-y-4" data-testid="event-detail-notfound">
        <p className="t-secondary">{t("agd.event_not_found")}</p>
        <Button variant="outline" className="border-app" onClick={() => navigate("/agenda")} data-testid="event-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("nav.agenda")}
        </Button>
      </div>
    );
  }
  if (!data) {
    return <p className="t-secondary text-sm py-10 text-center" data-testid="event-detail-loading">{t("agd.loading")}</p>;
  }

  const ev = data.event;
  const counts = data.counts;
  const my = data.my_status;
  const program = data.program || [];
  const d = new Date(ev.date + "T00:00:00");
  const isPast = (ev.end_date || ev.date) < new Date().toISOString().slice(0, 10);
  const itemMap = Object.fromEntries(invItems.map((i) => [i.id, i]));
  const commentRoots = (comments || []).filter((c) => !c.parent_comment_id);
  const repliesOf = (id) => (comments || []).filter((c) => c.parent_comment_id === id);

  const CommentItem = ({ c, isReply }) => (
    <div className={`py-2.5 ${isReply ? "ml-6 border-l-2 border-app pl-3" : ""}`} data-testid={`comment-${c.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-7 h-7 rounded-full brand-bg text-white text-[10px] font-bold flex items-center justify-center shrink-0" aria-hidden="true">
          {initials(c.author_name)}
        </span>
        <span className="text-sm font-semibold" data-testid={`comment-author-${c.id}`}>{c.author_name}</span>
        <span className="text-xs t-secondary">{relTime(c.created_at)}</span>
        <span className="flex gap-3 ml-auto">
          {!isReply && (
            <button className="text-xs t-secondary hover:text-white transition-colors" onClick={() => setReplyTo(c)} data-testid={`comment-reply-${c.id}`}>
              {t("agd.comment_reply")}
            </button>
          )}
          {(c.is_mine || isAdmin) && (
            <button className="text-xs t-secondary hover:text-rose-400 transition-colors" onClick={() => setDelComment(c)} data-testid={`comment-delete-${c.id}`}>
              {t("common.delete")}
            </button>
          )}
        </span>
      </div>
      <p className="text-sm mt-1 whitespace-pre-line break-words [overflow-wrap:anywhere]" data-testid={`comment-content-${c.id}`}>{c.content}</p>
    </div>
  );

  // Blocs de programme : mode Concert = liste unique ordonnée ; mode Répétition = par axe
  const blocks = [];
  if (ev.program_mode === "concert") {
    if (program.length) blocks.push({ axis: "", items: program });
  } else {
    program.forEach((p) => {
      const axis = p.axis || t("dash.program");
      let b = blocks.find((x) => x.axis === axis);
      if (!b) { b = { axis, items: [] }; blocks.push(b); }
      b.items.push(p);
    });
  }

  const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "");

  // Bascule rapide du mode d'affichage du programme (admin / responsable musical)
  const switchProgramMode = async () => {
    const nextMode = ev.program_mode === "concert" ? "repetition" : "concert";
    try {
      await api.put(`/events/${ev.id}/program`, { program, program_mode: nextMode });
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  // Partage du lien direct vers l'événement : feuille de partage native (mobile → SMS,
  // WhatsApp, e-mail…) ou copie presse-papiers (desktop). Tous les membres peuvent partager.
  const shareEvent = async () => {
    const url = `${window.location.origin}/agenda/${ev.id}`;
    const text = t("agd.share_text", { title: ev.title, date: d.toLocaleDateString(dateLocale(lang)) });
    if (navigator.share) {
      try { await navigator.share({ title: ev.title, text, url }); return; } catch (e) { if (e?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(t("agd.link_copied"));
  };

  // Export .ics : le mobile propose directement l'ajout à Apple Calendar / Google Calendar
  const downloadIcs = async () => {
    try {
      const r = await api.get(`/events/${ev.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ev.title}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("agd.ics_downloaded"));
    } catch (e) { toast.error(e.friendly); }
  };

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4 pb-10 min-w-0" data-testid="event-detail-page">
      {/* En-tête : retour + actions autorisées */}
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => navigate("/agenda")} className="flex items-center gap-1.5 text-sm t-secondary hover:text-white transition-colors py-2" data-testid="event-detail-back">
          <ArrowLeft className="h-4 w-4" /> {t("nav.agenda")}
        </button>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="border-app" onClick={shareEvent} data-testid="event-share-button" title={t("agd.share")} aria-label={t("agd.share")}>
            <Share2 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" className="border-app" onClick={downloadIcs} data-testid="event-ics-button" title={t("agd.add_calendar")} aria-label={t("agd.add_calendar")}>
            <CalendarPlus className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" className="border-app" onClick={() => navigate(`/agenda?edit=${ev.id}`)} data-testid="event-edit-button" title={t("agd.edit_event")}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" className="border-app text-amber-400" data-testid="event-remind-availability" title={t("agd.remind_avail")} aria-label={t("agd.remind_avail")}
                onClick={async () => {
                  try {
                    const r = await api.post(`/events/${ev.id}/remind-availability`);
                    toast.success(r.data.reminded ? t("agd.remind_avail_done", { n: r.data.reminded }) : t("agd.remind_avail_none"));
                  } catch (e) { toast.error(e.friendly); }
                }}>
                <Bell className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" className="border-app text-rose-400" onClick={() => setDelOpen(true)} data-testid="event-delete-button" title={t("common.delete")}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Photo / affiche de couverture (grande bannière, aucune zone vide si absente) */}
      {ev.cover && (
        <img
          src={eventCoverUrl(ev.id, ev.cover.updated_at)}
          alt={ev.title}
          className="w-full max-h-64 sm:max-h-80 object-cover rounded-2xl border border-app"
          data-testid="event-detail-cover"
          loading="lazy"
        />
      )}

      {/* Titre + type + organisateur */}
      <header>
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${CAT_CLS[mainOf(ev.category)]}`} data-testid="event-detail-category-chip">
          {ev.subcategory || t(`cat.${mainOf(ev.category)}`)}
        </span>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-2 break-words" data-testid="event-detail-title">{ev.title}</h1>
        {data.organizer_name && (
          <p className="text-sm t-secondary mt-1" data-testid="event-detail-organizer">{t("agd.organized_by", { name: data.organizer_name })}</p>
        )}
      </header>

      {/* Date et horaires */}
      <section className="app-card p-4 flex items-start gap-3" data-testid="event-detail-date">
        <CalendarDays className="h-5 w-5 accent shrink-0 mt-0.5" />
        <div>
          {ev.end_date ? (
            <p className="font-semibold capitalize" data-testid="event-detail-date-range">
              {t("agd.date_range", {
                start: d.toLocaleDateString(dateLocale(lang), { weekday: "long", day: "numeric", month: "long" }),
                end: new Date(ev.end_date + "T00:00:00").toLocaleDateString(dateLocale(lang), { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
              })}
            </p>
          ) : (
            <p className="font-semibold capitalize">{d.toLocaleDateString(dateLocale(lang), { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
          )}
          {(ev.start_time || ev.end_time) && (
            <p className="text-sm t-secondary">{ev.start_time}{ev.start_time && ev.end_time ? " – " : ""}{ev.end_time}</p>
          )}
        </div>
      </section>

      {/* Lieu + carte + itinéraire */}
      {ev.location && (
        <section className="app-card p-4" data-testid="event-detail-location">
          <button
            className={`flex items-start gap-3 w-full text-left ${hasGeo(ev.geo) ? "hover:opacity-80 transition-opacity" : "cursor-default"}`}
            onClick={() => hasGeo(ev.geo) && setNavGeo(ev.geo)}
            data-testid="event-detail-address"
            title={ev.geo?.address || ev.location}
          >
            <MapPin className="h-5 w-5 accent shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold">{ev.geo?.name || ev.location}</p>
              {hasGeo(ev.geo) && (
                <p className="text-sm t-secondary" data-testid="event-detail-address-lines">
                  {[ev.geo.address || [ev.geo.postcode, ev.geo.city].filter(Boolean).join(" "), ev.geo.country].filter(Boolean).join(" — ")}
                </p>
              )}
              {!hasGeo(ev.geo) && ev.geo?.name && ev.location !== ev.geo.name && <p className="text-sm t-secondary">{ev.location}</p>}
            </div>
          </button>
          {hasGeo(ev.geo) && (
            <>
              <div
                className="relative mt-3 rounded-xl overflow-hidden border border-app cursor-pointer group"
                onClick={() => setNavGeo(ev.geo)}
                data-testid="event-detail-map"
                title={t("agd.open_navigation")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setNavGeo(ev.geo)}
              >
                <iframe src={osmEmbedUrl(ev.geo.lat, ev.geo.lon)} title={ev.geo.name || ev.location} className="w-full h-48 sm:h-56 pointer-events-none" loading="lazy" tabIndex={-1} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </div>
              <Button className="brand-bg text-white mt-3" onClick={() => setNavGeo(ev.geo)} data-testid="event-detail-itinerary">
                <Navigation className="h-4 w-4 mr-1.5" /> {t("agd.itinerary")}
              </Button>
            </>
          )}
        </section>
      )}

      {/* Météo du lieu (secondaire, jamais bloquante — §21/22) */}
      {weather?.available && (
        <section className="app-card p-4 flex items-center gap-3" data-testid="event-weather">
          {(() => { const WIcon = WX_ICON[weather.kind] || Cloudy; return <WIcon className="h-7 w-7 accent shrink-0" />; })()}
          <div>
            <p className="font-semibold" data-testid="event-weather-figures">
              {weather.temp_max}°C <span className="t-secondary font-normal text-sm">/ {weather.temp_min}°C</span> • {weather.wind} km/h
            </p>
            <p className="text-sm t-secondary" data-testid="event-weather-label">{t(`wx.${weather.kind}`)}</p>
          </div>
        </section>
      )}
      {weather && !weather.available && weather.reason === "out_of_range" && hasGeo(ev.geo) && (
        <p className="text-xs t-secondary px-1" data-testid="event-weather-later">{t("agd.weather_later")}</p>
      )}

      {/* Groupe */}
      {group?.name && (
        <p className="flex items-center gap-2 text-sm t-secondary px-1" data-testid="event-detail-group">
          <Users className="h-4 w-4" /> {group.name}
        </p>
      )}

      {/* Description (retours à la ligne préservés) */}
      {(ev.notes || ev.detail) && (
        <section className="app-card p-4" data-testid="event-detail-description">
          <h2 className="text-xs font-bold uppercase tracking-wider t-secondary mb-2">{t("agd.description_label")}</h2>
          <p className="text-sm whitespace-pre-line break-words [overflow-wrap:anywhere]">{ev.notes || ev.detail}</p>
        </section>
      )}

      {/* Tenue / checklist */}
      {(ev.dress_code || (ev.dress_items || []).length > 0 || (ev.checklist || []).length > 0) && (
        <section className="app-card p-4 space-y-1.5" data-testid="event-detail-dress">
          {ev.dress_code && (
            <p className="text-sm flex items-center gap-2" data-testid="event-detail-dress-code">
              <Shirt className="h-4 w-4 accent shrink-0" /> <span className="t-secondary">{t("agd.dress_prefix")} {ev.dress_code}</span>
            </p>
          )}
          {(ev.dress_items || []).length > 0 && (
            <p className="text-xs t-secondary flex items-center gap-2 flex-wrap" data-testid="event-detail-dress-items">
              <Shirt className="h-4 w-4 shrink-0" /> {(ev.dress_items || []).map((id) => itemMap[id]?.name).filter(Boolean).join(", ")}
            </p>
          )}
          {(ev.checklist || []).length > 0 && (
            <p className="text-xs t-secondary flex items-center gap-2 flex-wrap" data-testid="event-detail-checklist">
              <ClipboardList className="h-4 w-4 shrink-0" /> {(ev.checklist || []).map((id) => itemMap[id]?.name).filter(Boolean).join(", ")}
            </p>
          )}
        </section>
      )}

      {/* Programme / setlist (liés à la Bibliothèque) */}
      {program.length > 0 && (
        <section className="app-card p-4" data-testid="event-detail-program">
          <div className="flex items-center gap-3 pb-3 border-b border-app">
            <span className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center shrink-0" aria-hidden="true">
              <ListMusic className="h-5 w-5 accent" />
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-lg font-bold truncate" data-testid="program-name">{ev.program_name || ev.title}</h2>
              <p className="text-xs t-secondary" data-testid="program-meta">{t("agd.program_summary", { n: program.length, mode: t(ev.program_mode === "concert" ? "agd.mode_concert" : "agd.mode_repetition") })}</p>
            </div>
            {canProgram && (
              <span className="flex items-center gap-1 shrink-0">
                <button className="p-2 t-secondary hover:text-white transition-colors" onClick={() => navigate(`/agenda?program=${ev.id}`)} data-testid="event-program-edit-button" title={t("agd.edit_program")}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button className="p-2 t-secondary hover:text-white transition-colors" onClick={switchProgramMode} data-testid="event-program-mode-switch" title={t("agd.switch_mode")}>
                  <ArrowLeftRight className="h-4 w-4" />
                </button>
              </span>
            )}
          </div>
          <div className="space-y-4 mt-3">
            {blocks.map((b) => (
              <div key={b.axis} data-testid={`program-block-${b.axis || "concert"}`}>
                {b.axis && (
                  <p className="flex items-center gap-2 font-heading font-bold mb-2" data-testid={`program-block-title-${b.axis}`}>
                    <span className="w-1.5 h-5 rounded-full accent-bg" aria-hidden="true" />
                    {b.axis} <span className="text-sm font-normal t-secondary">({b.items.length})</span>
                  </p>
                )}
                <ol className="space-y-2">
                  {b.items.map((p, j) => (
                    <li key={j}>
                      <button className="w-full text-left flex items-center gap-3 border border-app rounded-xl px-3.5 py-2.5 hover:bg-white/5 transition-colors" onClick={() => navigate(`/bibliotheque?node=${p.node_id}`)} data-testid={`program-piece-${b.axis}-${j}`}>
                        <Music2 className="h-4 w-4 accent shrink-0" />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold uppercase tracking-wide truncate">{p.name}</span>
                          {[p.composer, fmtDur(p.duration_sec)].filter(Boolean).length > 0 && (
                            <span className="block text-xs t-secondary truncate">{[p.composer, fmtDur(p.duration_sec)].filter(Boolean).join(" · ")}</span>
                          )}
                        </span>
                        <FolderOpen className="h-4 w-4 t-secondary shrink-0" />
                      </button>
                      {p.note && <span className="block text-xs t-secondary italic ml-7 mt-1">→ {p.note}</span>}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Réponse du membre : grandes actions tactiles */}
      {!isPast && (
        <section data-testid="event-detail-answer">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => answer("yes")}
              className={`py-4 rounded-xl border font-semibold flex items-center justify-center gap-2 transition-colors ${
                my === "yes" ? "bg-emerald-500 text-white border-transparent" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
              }`}
              data-testid="answer-yes-button"
            >
              <Check className="h-5 w-5" /> {t("agd.participate")}
            </button>
            <button
              onClick={() => answer("no")}
              className={`py-4 rounded-xl border font-semibold flex items-center justify-center gap-2 transition-colors ${
                my === "no" ? "bg-rose-500 text-white border-transparent" : "border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
              }`}
              data-testid="answer-no-button"
            >
              <XIcon className="h-5 w-5" /> {t("agd.decline")}
            </button>
          </div>
          <button
            onClick={() => answer("maybe")}
            className={`mt-2 w-full py-2.5 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              my === "maybe" ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "border-app t-secondary hover:bg-white/5"
            }`}
            data-testid="answer-maybe-button"
          >
            <HelpCircle className="h-4 w-4" /> {t("avail.maybe")}
          </button>
        </section>
      )}

      {/* Synthèse des réponses */}
      <section className="app-card p-4" data-testid="responses-summary">
        <h2 className="text-xs font-bold uppercase tracking-wider t-secondary mb-3">{t("agd.responses_title")}</h2>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div data-testid="resp-yes-count">
            <Check className="h-5 w-5 mx-auto text-emerald-400" />
            <p className="text-xl font-bold text-emerald-400 mt-1">{counts.yes}</p>
            <p className="text-xs t-secondary">{t("agd.resp_yes_label")}</p>
          </div>
          <div data-testid="resp-pending-count">
            <HelpCircle className="h-5 w-5 mx-auto text-amber-400" />
            <p className="text-xl font-bold text-amber-400 mt-1">{counts.unanswered}</p>
            <p className="text-xs t-secondary">{t("agd.resp_pending_label")}</p>
          </div>
          <div data-testid="resp-no-count">
            <XIcon className="h-5 w-5 mx-auto text-rose-400" />
            <p className="text-xl font-bold text-rose-400 mt-1">{counts.no}</p>
            <p className="text-xs t-secondary">{t("agd.resp_no_label")}</p>
          </div>
        </div>
        <Button variant="outline" className="w-full mt-4 border-app" onClick={() => navigate(`/presence?event=${ev.id}`)} data-testid="see-presences-button">
          {t("agd.see_presences")}
        </Button>
      </section>

      {/* Commentaires liés à l'événement (sans notifications — §38) */}
      <section className="app-card p-4" data-testid="event-comments">
        <h2 className="text-xs font-bold uppercase tracking-wider t-secondary mb-2 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" /> {t("agd.comments_title")}{comments ? ` (${comments.length})` : ""}
        </h2>
        {comments === null && <p className="text-sm t-secondary">{t("agd.loading")}</p>}
        {comments !== null && comments.length === 0 && (
          <p className="text-sm t-secondary py-1" data-testid="comments-empty">{t("agd.no_comments")}</p>
        )}
        {commentRoots.map((c) => (
          <div key={c.id}>
            <CommentItem c={c} />
            {repliesOf(c.id).map((r) => <CommentItem key={r.id} c={r} isReply />)}
          </div>
        ))}
        {replyTo && (
          <div className="flex items-center justify-between text-xs t-secondary border border-app rounded-lg px-3 py-1.5 mb-2" data-testid="comment-replying-to">
            <span>{t("agd.comment_reply_to", { name: replyTo.author_name })}</span>
            <button onClick={() => setReplyTo(null)} data-testid="comment-reply-cancel" aria-label={t("confirm.cancel")}>
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <div className="flex gap-2 mt-3">
          <Input
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendComment(); } }}
            placeholder={t("agd.comment_placeholder")}
            className="bg-transparent border-app"
            data-testid="comment-input"
          />
          <Button className="brand-bg text-white shrink-0" onClick={sendComment} disabled={!commentText.trim()} data-testid="comment-send" aria-label={t("agd.comment_send")}>
            <SendHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </section>

      <NavChooser geo={navGeo} onClose={() => setNavGeo(null)} />

      <ConfirmDialog
        open={!!delComment}
        title={t("common.delete")}
        description={t("agd.comment_delete_desc")}
        onConfirm={doDeleteComment}
        onClose={() => setDelComment(null)}
        testid="delete-comment-confirm-dialog"
      />

      <ConfirmDialog
        open={!!delOpen}
        title={t("agd.delete_event")}
        description={t("agd.delete_event_desc", { name: ev.title })}
        onConfirm={doDelete}
        onClose={() => setDelOpen(false)}
        testid="delete-event-confirm-dialog"
      />
    </div>
  );
}
